import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { EDT_KEY } from '@teetsh/app/src/queries/edt/useEDTs';
import { EDTEvent, EmploiDuTemps } from '@teetsh/app/src/types/edt';
import { addToEDTPast } from '@teetsh/app/src/queries/edt/historyAtoms';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import removeSeanceClassroomFromSeances from '@teetsh/app/src/queries/edt/removeSeanceClassroomFromSeances';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

interface Props {
  seanceId: string;
  classroomId?: string;
}

export default function useDeleteEDTSeance(
  edtId: string,
  disableUndoRedo: boolean = false,
) {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['emploiDuTemps']);
  const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}`;
  const [, addToPast] = useAtom(addToEDTPast);
  const { mutateAsync } = useMutation({
    mutationFn: ({ seanceId, classroomId }: Props) =>
      classroomId
        ? request(
            `${requestURL}/classrooms/${classroomId}/edt-seances/${seanceId}`,
            {
              method: 'DELETE',
            },
          )
        : request(`${requestURL}/edt-seances/${seanceId}`, {
            method: 'DELETE',
          }),
    onMutate: ({ seanceId, classroomId }: Props) => {
      if (!disableUndoRedo) {
        const oldEDT = queryClient.getQueryData<EmploiDuTemps>([
          schoolyear.id,
          school.id,
          EDT_KEY,
          edtId,
        ]);
        if (oldEDT) {
          addToPast({ edtId: oldEDT.id, seances: oldEDT.events });
        }
      }
      queryClient.setQueryData<EmploiDuTemps>(
        [schoolyear.id, school.id, EDT_KEY, edtId],
        (oldEDT: EmploiDuTemps) => {
          const oldEvents = oldEDT?.events || [];
          return {
            ...oldEDT,
            events: classroomId
              ? (removeSeanceClassroomFromSeances(
                  oldEvents,
                  seanceId,
                  classroomId,
                ) as EDTEvent[])
              : oldEvents.filter(evt => seanceId !== evt.id),
          };
        },
      );
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: [schoolyear.id, school.id, EDT_KEY, edtId],
      });

      notifyError({
        title: t('emploiDuTemps:messages.seance.deleteError_one'),
      });
    },
  });
  return mutateAsync;
}
